// Transaction logs › How it's calculated (wave 3, W2 look): what each
// Finance dashboard figure means, where its data comes from and the exact
// formula. Reference text for finance staff; the words and formulas are the
// ones finance signed off, only the look changed (cards with an icon on a
// tinted circle, formulas in a quiet box, theme colours).
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { tokens as c } from '../../theme';
import { IconCircle, StatusChip } from '../../components/modern';
import { SOURCE_BUCKETS, BOOKING_SOURCES } from './sourceBuckets';

const textSx = { fontSize: 14, color: c.INK, lineHeight: 1.7 };
const quietSx = { fontSize: 13.5, color: c.INK_2, lineHeight: 1.6 };

function Section({ icon, hue, title, children }) {
  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <IconCircle icon={icon} hue={hue} size={34} />
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4, color: c.INK, minWidth: 0 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={textSx}>{children}</Box>
    </Paper>
  );
}

// A formula, kept on its own lines in a quiet box (a <pre>, so the columns line up).
function Formula({ children }) {
  return (
    <Box
      component="pre"
      sx={{
        fontSize: 13,
        lineHeight: 1.6,
        bgcolor: c.SURFACE_ALT,
        border: `1px solid ${c.OUTLINE}`,
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        my: 1,
        mx: 0,
        color: c.INK,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </Box>
  );
}

// One figure: its name, formula, meaning, data source and a caveat.
function Metric({ name, formula, what, where, caveat }) {
  return (
    <Box sx={{ mb: 2.25, pl: 1.5, borderLeft: `3px solid ${c.OUTLINE_STRONG}` }}>
      <Typography component="h3" sx={{ fontWeight: 700, color: c.INK, fontSize: 15.5, mb: 0.5 }}>
        {name}
      </Typography>
      <Formula>{formula}</Formula>
      <Typography sx={{ fontSize: 14, color: c.INK, mb: 0.5, lineHeight: 1.6 }}>
        <strong>What it means:</strong> {what}
      </Typography>
      <Typography sx={{ ...quietSx, mb: caveat ? 0.5 : 0 }}>
        <strong>Data source:</strong> {where}
      </Typography>
      {caveat ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: c.WARNING_TEXT, fontSize: 13.5, lineHeight: 1.55, mt: 0.5 }}>
          <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 18, mt: '1px', flex: 'none' }} />
          <span>{caveat}</span>
        </Box>
      ) : null}
    </Box>
  );
}

const BUCKET_TEXT = {
  regular_booking: 'Standard dispatched trip — customer requested, driver accepted via /DriverTrips.',
  donkey_meter: 'Multi-customer time-based meter trip (DonkeyMeterScreen). Cash only, two-phase completion.',
  offline_meter: 'Single-customer offline meter (OfflineMeterScreen). Sync queue processes when network returns.',
  shared_trip: 'Driver-initiated multi-customer street pickup (ShareTripScreen). Each customer at a pre-negotiated cash price.',
  top_up: 'Driver added cash to their top-up balance via payment gateway (Chapa, Telebirr, Santimpay, PayPal) or admin manual entry.',
  admin_credit: 'Manual credit applied by admin (referral bonuses, refunds, cancelled-prepaid refunds, signup credit).',
  cancellation_fee: 'Cancellation slab fee — paid by customer, credited to driver, per the per-car-type cancellation policy.',
  tip: 'Customer-added tip on top of the trip fare. Credited fully to the driver.',
  weekly_goal_bonus: 'Weekly trip goal incentive bonus credited per the admin Driver Incentives configuration.',
  mile_redeem: 'Donkey Miles redeemed for wallet credit at the configured redeem rate.',
  mile_airtime: 'Donkey Miles converted to prepaid airtime voucher codes (Ethio Telecom / Safaricom). Amount = card face value; no wallet money moves — the card inventory on the Phone Cards page decrements.',
  mile_donation: 'Donkey Miles donated to a partner NGO (managed on the NGOs page), valued at the wallet redeem rate. No wallet money moves.',
  meter_reward: 'Free airtime card auto-awarded every Nth completed Donkey Meter trip (configured in Settings → Donkey Miles). Amount = card face value; consumes Phone Cards inventory.',
  wallet_transfer: 'Driver moved money between their walletBalance (earnings) and topUpBalance (commission float). Internal — net zero.',
  withdraw: 'Driver requested cash payout to their bank account.',
  withdraw_refund: 'Admin reversed/cancelled a pending withdrawal — funds returned to driver wallet.',
  welcome_credit: "One-time free flat-rate day(s) credited to a new driver's top-up balance when they finish the in-app onboarding wizard (Settings → flat-rate section). Non-withdrawable; consumed by the daily flat-rate sweeper.",
  holiday_promo: 'New Year registration gift: the one-time prepaid-day credit granted at sign-up to drivers who registered during the campaign window (Settings → New Year gift). Shares the once-per-phone ledger with the welcome credit, so a driver never receives both.',
  other: 'Unclassified entry. If you see many of these, a new source bucket may be needed.',
};

const MetricsGlossary = ({ settings }) => {
  const { t, i18n } = useTranslation();
  const sym = settings?.symbol || 'ETB';
  const sourceLabel = (key) => t(`tx_source_${key}`, { defaultValue: SOURCE_BUCKETS[key].label });

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Section icon={<MenuBookOutlinedIcon />} hue="info" title="How the Dashboard Numbers Are Calculated">
        <Typography sx={{ ...textSx, mb: 1 }}>
          Every number on the Finance Dashboard is computed from <strong>/transactionStats</strong>,
          a per-day pre-aggregated snapshot of wallet activity. The snapshot is updated in real time
          by the <code>bumpTransactionStat</code> Cloud Function whenever a wallet write happens
          (commission deduction, top-up, withdrawal, etc.), so reading these stats costs one tiny
          network call regardless of how many underlying transactions exist.
        </Typography>
        <Typography sx={{ ...textSx, mb: 1 }}>The data shape is:</Typography>
        <Formula>
{`/transactionStats/
  2026-05-25/
    regular_booking/  { Credit: 12340, Debit: 13400 }
    donkey_meter/     { Credit: 880,   Debit: 957 }
    top_up/           { Credit: 5000 }
    withdraw/         { Debit:  3200 }
    tip/              { Credit: 340 }
    ...`}
        </Formula>
        <Typography sx={{ ...quietSx, mt: 1 }}>
          Currency is whichever you've configured in Settings — currently <strong>{sym}</strong>.
          Dates are bucketed in <strong>Addis Ababa time (UTC+3)</strong> so "Today" matches what
          your team intuitively means.
        </Typography>
      </Section>

      <Section icon={<PaymentsOutlinedIcon />} hue="accent" title="Primary KPIs">
        <Metric
          name="Platform Commission"
          formula={`commission = GMV − Driver Earnings
         = Σ Debits[booking sources] − Σ Credits[booking sources]`}
          what="The platform's actual revenue — the slice of every fare that stays with us. This is the headline number for revenue tracking, board reporting, and runway math."
          where="Computed by subtracting driver_share Credits from total fare Debits, both filtered to the four booking sources (regular_booking, donkey_meter, shared_trip, offline_meter)."
          caveat="For cash trips, the driver collected the full fare and the commission was deducted from their pre-paid top-up balance. The 'Debit' entry represents the cash they collected — that's where the GMV side of this formula comes from."
        />
        <Metric
          name="GMV (Gross Merchandise Value)"
          formula={`GMV = Σ Debits where source ∈ {regular_booking, donkey_meter, shared_trip, offline_meter}`}
          what="Total fare value of all trips — the size of the marketplace. Use this to track marketplace growth independent of commission rate changes."
          where="Cash trips contribute via the driver's wallet Debit (= cash collected, equal to trip_cost). Prepaid trips contribute via the customer's wallet Debit (= trip_cost − discount). Both land in the same source bucket."
        />
        <Metric
          name="Driver Earnings"
          formula={`Driver Earnings = Σ Credits where source ∈ {booking sources}`}
          what="What drivers got paid for trips (driver_share recognized). Equals GMV minus commission. Useful for driver-supply-side analysis and tax reporting."
          where="Every completed booking writes one Credit on the driver's wallet history equal to driver_share. This sums those Credits across all booking sources."
        />
      </Section>

      <Section icon={<LocalAtmOutlinedIcon />} hue="success" title="Cash Flow Metrics">
        <Metric
          name="Top-Up Inflow"
          formula={`Top-Up = Σ Credits where source = 'top_up'`}
          what="Cash drivers PUT INTO the platform float (via Chapa, Telebirr, Santimpay, PayPal, or admin manual top-up). This is the platform's working capital — it's what funds commission deductions for cash trips."
          where="Every payment-gateway success writes a Credit with source='top_up' on the driver's wallet. Admin manual top-ups (via the admin panel) also tag as top_up."
          caveat="A healthy ratio of (Top-Up Inflow ÷ Driver Earnings) should approximate your commission rate. If it drifts much lower, drivers are accumulating commission debt; if much higher, they're holding excess float."
        />
        <Metric
          name="Withdrawals"
          formula={`Withdrawals = Σ (Credit + Debit) where source = 'withdraw'`}
          what="Cash drivers pulled OUT to their bank accounts. Drives float management — high withdrawal velocity means the platform needs more working capital."
          where="When a driver requests a withdrawal, a 'Processing' entry is written to walletHistory with source='withdraw' and an amount = withdrawal request."
        />
        <Metric
          name="Volume"
          formula={`Volume = Σ (Credit + Debit) across all sources`}
          what="Raw transaction throughput — total amount that moved through the platform. Engagement indicator, NOT a revenue metric. (Cash trips double-count here — once as Debit, once as Credit.)"
          where="Sum of every Credit and every Debit in the period, across every source bucket."
          caveat="Don't compare Volume to GMV directly — they measure different things. Volume is a leading indicator of activity; GMV is the actual money the marketplace processed."
        />
      </Section>

      <Section icon={<TrendingUpRoundedIcon />} hue="info" title="Trend Deltas (the ↑ / ↓ percentages on each tile)">
        <Formula>{`Δ% = ((current − previous) / |previous|) × 100`}</Formula>
        <Typography sx={{ ...textSx, mb: 1 }}>The comparison window varies by tile:</Typography>
        <Box component="ul" sx={{ ...textSx, pl: 2.5, mt: 0, mb: 1 }}>
          <li><strong>Today tiles</strong> compare to <em>yesterday</em>.</li>
          <li><strong>This Week tiles</strong> compare to the <em>previous 7-day window</em> (day-of-week-aligned).</li>
          <li><strong>This Month tiles</strong> compare to <em>last month's same-period totals</em>.</li>
        </Box>
        <Typography sx={quietSx}>
          Deltas are bounded to ±999% so a tiny base doesn't produce noise like "+18,500%". If the
          previous period was zero, you'll see ↑ new instead of a percentage.
        </Typography>
      </Section>

      <Section icon={<BoltRoundedIcon />} hue="amber" title="Spike Detection (the SPIKE badge on Top Days)">
        <Formula>{`isSpike = day_commission > mean + 1.5 × σ
   where mean, σ are calculated over commission-positive days in the 30-day window`}</Formula>
        <Typography sx={textSx}>
          A day is flagged as a SPIKE when its <strong>commission</strong> exceeds the 30-day mean by
          more than 1.5 standard deviations. In practice this catches promo-day surges, airport rushes,
          sporting event Saturdays, or unusually busy holiday eves. It will NOT flag every above-average
          day — it's designed for the genuinely unusual ones that moved the bottom line.
        </Typography>
      </Section>

      <Section icon={<LabelOutlinedIcon />} hue="neutral" title="Source Bucket Reference">
        <Typography sx={{ ...quietSx, mb: 1.5 }}>
          Every wallet transaction is bucketed into one of these source categories. Booking sources
          contribute to GMV/commission; the others are operational cash flows.
        </Typography>
        <Box component="dl" sx={{ m: 0 }}>
          {Object.keys(SOURCE_BUCKETS).map((key) => {
            const isBooking = BOOKING_SOURCES.indexOf(key) >= 0;
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, borderTop: `1px solid ${c.OUTLINE}`, '&:first-of-type': { borderTop: 0 } }}>
                <Box aria-hidden="true" sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: SOURCE_BUCKETS[key].color, mt: '4px', flex: 'none' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="dt" sx={{ fontSize: 14, fontWeight: 600, color: c.INK, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    {sourceLabel(key)}
                    {isBooking ? <StatusChip tone="info" label={t('fin_booking_source', { defaultValue: 'Booking source' })} /> : null}
                  </Typography>
                  <Typography component="dd" sx={{ ...quietSx, m: 0 }}>
                    {BUCKET_TEXT[key] || ''}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Section>

      <Section icon={<CalculateOutlinedIcon />} hue="accent" title="Worked Example (a single trip)">
        <Typography sx={{ ...textSx, mb: 1 }}>
          A 100 {sym} cash trip with an 8% commission rate (driver_share = 92 {sym}, commission = 8 {sym}):
        </Typography>
        <Formula>
{`Wallet history writes for this trip:
  Debit on driver:  100 ${sym}  source=regular_booking   (cash collected)
  Credit on driver:  92 ${sym}  source=regular_booking   (driver_share)

/transactionStats bumps:
  regular_booking/Debit  += 100
  regular_booking/Credit += 92

Dashboard reads:
  GMV               = 100 ${sym}   (the Debit)
  Driver Earnings   = 92 ${sym}    (the Credit)
  Platform Commission = 100 − 92 = 8 ${sym}   ✓
  Volume            = 100 + 92 = 192 ${sym}   (engagement, not revenue)`}
        </Formula>
        <Typography sx={{ ...textSx, mt: 1.5, mb: 1 }}>
          For a 100 {sym} <strong>prepaid wallet</strong> trip (no cash, customer wallet charged):
        </Typography>
        <Formula>
{`Wallet history writes for this trip:
  Debit on customer: 100 ${sym}  source=regular_booking   (customer wallet charge)
  Credit on driver:   92 ${sym}  source=regular_booking   (driver_share)

Same final numbers:
  GMV               = 100 ${sym}   (same Debit, just from a different wallet)
  Driver Earnings   = 92 ${sym}
  Platform Commission = 8 ${sym}   ✓`}
        </Formula>
        <Typography sx={{ ...quietSx, mt: 1.5 }}>
          Notice how the same accounting identity holds across payment methods — that's why the
          Debit-side aggregation correctly captures GMV regardless of whether the trip was paid in
          cash or prepaid wallet.
        </Typography>
      </Section>

      <Section icon={<SettingsSuggestOutlinedIcon />} hue="neutral" title="Operational Notes for Finance">
        <Box component="ul" sx={{ ...textSx, pl: 2.5, my: 0 }}>
          <li>
            <strong>If Platform Commission looks wrong / stale:</strong> hit
            the "Backfill Stats" button on the Security Dashboard. It re-aggregates from
            walletHistory and overwrites /transactionStats. Idempotent — safe to re-run.
          </li>
          <li>
            <strong>If commission for a specific trip looks off:</strong> open the
            Transaction Logs tab, filter by driver mobile or transaction ID. The detail
            rows always come from walletHistory, which is the source of truth.
          </li>
          <li>
            <strong>Negative deltas (↓ red):</strong> investigate — is this an outage,
            a holiday, a driver-supply problem, or a market correction? Cross-reference
            with the Transaction Logs tab to find specific drivers / time windows.
          </li>
          <li>
            <strong>Float health check:</strong> Top-Up Inflow ÷ Driver Earnings ≈ your
            commission rate. Sustained drift below means drivers are accumulating
            commission debt (negative top-up balance).
          </li>
        </Box>
      </Section>
    </Box>
  );
};

export default MetricsGlossary;
